import { isValidElement, type CSSProperties, type ReactNode } from "react";
import { CheckIcon, CornerDownRightIcon } from "lucide-react";

import { Icon } from "@/components/atoms/Icon";
import { State } from "@/state";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/components/ui/utils";
import { Typography } from "@/components/ui/typography";
import { useSelectableListStyle } from "./SelectableList";

import type { SelectableListSize } from "./SelectableRow";
import { Checkbox } from "@/components/ui/checkbox";

/** Fixed line heights inside each row (title + description + optional summary). */
export const TITLE_LINE_HEIGHT = 22;
const DESCRIPTION_LINE_HEIGHT = 20;
export const OVERLINE_LINE_HEIGHT = 16;
const SUMMARY_LINE_HEIGHT = 18;
const FOOTER_LINE_HEIGHT = 28;
const CONTENT_GAP = 0;
const CHECKBOX_SIZE = 20;
/** ``SelectableCardList`` default click-target ``py: 1`` (theme spacing × 2). */
const DEFAULT_ROW_PADDING_Y = 16;

/**
 * Structured trailing cells for ``overlineEnd`` / ``titleEnd`` / ``descriptionEnd``.
 * When any row uses ``delta``, the row HOC lays content out as a shared CSS grid
 * so deltas and values stay vertically aligned and right-justified.
 * Chips / enums belong in ``footerEnd`` (bottom-right), not here.
 */
export type SelectableListItemRowEnd = {
  /** Delta / exact-match indicator — shared right-aligned column. */
  delta?: ReactNode;
  /** Primary trailing value (date, amount, timestamp). */
  value?: ReactNode;
};

function isSelectableListItemRowEnd(
  end: ReactNode | SelectableListItemRowEnd,
): end is SelectableListItemRowEnd {
  return (
    end != null &&
    typeof end === "object" &&
    !isValidElement(end) &&
    !Array.isArray(end) &&
    ("delta" in end || "value" in end)
  );
}

function normalizeRowEnd(
  end: ReactNode | SelectableListItemRowEnd | null | undefined,
): SelectableListItemRowEnd | null {
  if (end == null || end === false || end === true) {
    return null;
  }
  if (isSelectableListItemRowEnd(end)) {
    if (end.delta == null && end.value == null) {
      return null;
    }
    return end;
  }
  return { value: end };
}

export type SelectableListItemRowChangeStats = {
  added: number;
  changed: number;
  removed: number;
};

const CHANGE_STAT_CONFIG = [
  {
    key: "added" as const,
    symbol: "+",
    colorClassName: "text-foreground",
  },
  {
    key: "changed" as const,
    symbol: "~",
    colorClassName: "text-foreground",
  },
  {
    key: "removed" as const,
    symbol: "-",
    colorClassName: "text-destructive",
  },
];

function descriptionBlockHeight(maxLines: number) {
  return DESCRIPTION_LINE_HEIGHT * maxLines;
}

function summaryBlockHeight(maxLines: number) {
  return SUMMARY_LINE_HEIGHT * maxLines;
}

export function getSelectableListItemRowContentHeight(
  descriptionMaxLines = 1,
  options?: {
    summaryMaxLines?: number;
    overline?: boolean;
    footer?: boolean;
  },
) {
  const summaryMaxLines = options?.summaryMaxLines ?? 0;
  const overlineHeight = options?.overline
    ? OVERLINE_LINE_HEIGHT + CONTENT_GAP
    : 0;
  const descriptionHeight =
    descriptionMaxLines > 0
      ? CONTENT_GAP + descriptionBlockHeight(descriptionMaxLines)
      : 0;
  const footerHeight = options?.footer ? CONTENT_GAP + FOOTER_LINE_HEIGHT : 0;
  const textHeight =
    overlineHeight +
    TITLE_LINE_HEIGHT +
    descriptionHeight +
    footerHeight +
    (summaryMaxLines > 0
      ? CONTENT_GAP + summaryBlockHeight(summaryMaxLines)
      : 0);

  return Math.max(CHECKBOX_SIZE, textHeight);
}

export function getSelectableListItemRowHeight(
  descriptionMaxLines = 1,
  options?: {
    summaryMaxLines?: number;
    overline?: boolean;
    footer?: boolean;
    /** Vertical padding included in the click target (default ``py: 1`` → 16). */
    paddingY?: number;
  },
) {
  const { paddingY = DEFAULT_ROW_PADDING_Y, ...contentOptions } = options ?? {};
  return (
    getSelectableListItemRowContentHeight(descriptionMaxLines, contentOptions) +
    paddingY
  );
}

/** Inner content height; keep in sync with ``SelectableCardList`` row padding (``py: 1`` × 2). */
export const SELECTABLE_LIST_ITEM_ROW_CONTENT_HEIGHT =
  getSelectableListItemRowContentHeight(1);

/** Total virtualized row size including ``SelectableCardList`` click-target padding. */
export const SELECTABLE_LIST_ITEM_ROW_HEIGHT =
  getSelectableListItemRowHeight(1);

/** Row height when an overline (e.g. breadcrumb) sits above the title. */
export const SELECTABLE_LIST_ITEM_ROW_WITH_OVERLINE_HEIGHT =
  getSelectableListItemRowHeight(1, { overline: true });

/** Triage match-option rows — overline + up to 2 description lines (notes + contact) + chips. */
export const SELECTABLE_LIST_ITEM_ROW_TRIAGE_OPTION_HEIGHT =
  getSelectableListItemRowHeight(2, {
    overline: true,
    footer: true,
  });

/** Taller row preset for entity overline + title + message + summary (thread list). */
export const SELECTABLE_LIST_ITEM_ROW_THREAD_HEIGHT =
  getSelectableListItemRowHeight(1, { summaryMaxLines: 2, overline: true });

/** Triage list row height — description fits charted account + status chips. */
export const SELECTABLE_LIST_ITEM_ROW_TRIAGE_HEIGHT =
  getSelectableListItemRowHeight(2);

export function getPendingChangesStats(
  changes?: Array<{
    changeType?: string | null;
    status?: string | null;
    deltas?: { fieldName: string }[] | null;
  }> | null,
): SelectableListItemRowChangeStats {
  const stats: SelectableListItemRowChangeStats = {
    added: 0,
    changed: 0,
    removed: 0,
  };

  for (const change of changes ?? []) {
    if (change.status && change.status !== "pending") continue;
    if (change.changeType === "create") stats.added += 1;
    else if (change.changeType === "delete") stats.removed += 1;
    else if (change.changeType === "update") {
      stats.changed += change.deltas?.length ?? 1;
    }
  }

  return stats;
}

export function getPendingChangeStats(
  pendingChange?: {
    changeType?: string | null;
    status?: string | null;
    deltas?: { fieldName: string }[] | null;
    children?:
      | {
          changeType?: string | null;
          status?: string | null;
          deltas?: { fieldName: string }[] | null;
        }[]
      | null;
  } | null,
): SelectableListItemRowChangeStats {
  return getPendingChangesStats(
    pendingChange ? [pendingChange, ...(pendingChange.children ?? [])] : [],
  );
}

function SelectableListItemRowChangeStack({
  stats,
}: {
  stats: SelectableListItemRowChangeStats;
}) {
  return (
    <div className="flex flex-row items-center gap-1.5 shrink-0">
      {CHANGE_STAT_CONFIG.map(({ key, symbol, colorClassName }) => {
        const count = stats[key];
        return (
          <Typography
            key={key}
            level="body-xs"
            // On the title's line (``TITLE_LINE_HEIGHT``).
            className={cn(
              "min-w-fit [line-height:22px] font-mono [font-weight:700] text-[11px] [font-variant-numeric:tabular-nums]",
              count > 0 ? colorClassName : "text-muted-foreground/80",
              count > 0 ? "opacity-[1]" : "opacity-[0.45]",
            )}
          >
            {symbol}
            {count}
          </Typography>
        );
      })}
    </div>
  );
}

export type SelectableListItemRowProps = {
  /** Optional line above the title (breadcrumbs, path, …). */
  overline?: ReactNode;
  /**
   * Right side of the overline row.
   * Pass ``SelectableListItemRowEnd`` to participate in the shared trailing grid.
   */
  overlineEnd?: ReactNode | SelectableListItemRowEnd;
  /** Primary label — one line, ellipsized when a string. */
  title: ReactNode;
  /** Secondary line under the title — entity name, id, status icons, etc. */
  description?: ReactNode;
  /** Max lines for ``description`` before clamping (default 1). */
  descriptionMaxLines?: 1 | 2;
  /** Optional tertiary summary line under ``description`` (``body-sm``). */
  summary?: string | null;
  /** Max lines for ``summary`` before clamping (default 2). */
  summaryMaxLines?: 1 | 2;
  /**
   * Right side of the title row (time, amount, …).
   * Pass ``SelectableListItemRowEnd`` to participate in the shared trailing grid.
   */
  titleEnd?: ReactNode | SelectableListItemRowEnd;
  /**
   * Right side of the description row.
   * Pass ``SelectableListItemRowEnd`` to participate in the shared trailing grid.
   */
  descriptionEnd?: ReactNode | SelectableListItemRowEnd;
  /**
   * Bottom-right chips / enums (``SimpleEnum``, confidence, tags, …).
   * Renders on the footer row, right-aligned opposite ``description``.
   */
  footerEnd?: ReactNode;
  /**
   * Full-width block under the title / description grid (e.g. linked
   * document tiles). Sits in the text column to the right of ``leading``.
   */
  below?: ReactNode;
  /** When set, replaces ``description`` and renders in danger color. */
  error?: string | null;
  /** Use semibold title weight (unread / selected items). */
  emphasized?: boolean;
  /** Muted title color (e.g. read threads). */
  mutedTitle?: boolean;
  /**
   * Optional leading slot before the text column (overrides checkbox, which
   * - where the row has an overline - is at the overline's start, the words
   * under it at the row's own edge).
   */
  leading?: ReactNode;
  checkbox?: {
    checked: boolean;
    onToggle?: () => void;
  };
  /** Optional slot at the end of the row (e.g. a "…" menu button). */
  trailing?: ReactNode;
  /** When set, renders + / ~ / - counts on the title row (after ``titleEnd``). */
  changeStats?: SelectableListItemRowChangeStats;
  /**
   * The row stands in for one while its list loads: it draws what it is
   * given, as it always does, with each piece wrapped in ``Skeleton`` -
   * so every bar is the size of the piece it hides, and the row the size of
   * the row it stands for. Give it a typical row's content.
   */
  isLoading?: boolean;
  /**
   * What the text is set at. Read from the list the row is in
   * (``SelectableListStyleContext``); given here only for a row outside one.
   */
  size?: SelectableListSize;
  rootDataAttrs?: Record<string, string | undefined>;
};

/**
 * A block piece of a loading row - a control, a chip, a value, a node the
 * caller passes - wrapped in ``Skeleton``: the bar is the piece's own
 * box. The wrapper takes the piece's place in its cell and lays the piece out
 * as the cell would, so nothing moves.
 */
export const SKELETON_PIECE_SX = {
  width: "fit-content",
  maxWidth: "100%",
  display: "flex",
  flexDirection: "inherit",
  flexWrap: "inherit",
  alignItems: "inherit",
  justifyContent: "inherit",
  gap: "inherit",
} as const;

/** ``SKELETON_PIECE_SX`` as classes, for a piece drawn with them. */
export const SKELETON_PIECE_CLASS =
  "w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]";

function TrailingCells({
  end,
  useDeltaGrid,
  action,
}: {
  end: SelectableListItemRowEnd | null;
  useDeltaGrid: boolean;
  /** Control after the value (the row's ``trailing``). */
  action?: ReactNode;
}) {
  // Zero-height so a button doesn't grow the text line it sits on.
  const actionNode =
    action != null ? (
      <div className="h-0 self-center flex items-center ml-1.5">{action}</div>
    ) : null;

  if (!useDeltaGrid) {
    if (end?.value == null && actionNode == null) {
      return <div className="w-0 shrink-0" />;
    }
    return (
      <div className="[justify-self:end] self-baseline text-right min-w-0 flex items-baseline">
        {end?.value}
        {actionNode}
      </div>
    );
  }

  return (
    <>
      <div className="[justify-self:end] self-baseline text-right min-w-0 flex items-baseline justify-end [font-variant-numeric:tabular-nums]">
        {end?.delta ?? null}
      </div>
      <div className="[justify-self:end] self-baseline text-right min-w-0 flex items-baseline justify-end">
        {end?.value ?? null}
        {actionNode}
      </div>
    </>
  );
}

export function SelectableListItemRow({
  overline,
  overlineEnd,
  title,
  description,
  descriptionMaxLines = 1,
  summary,
  summaryMaxLines = 2,
  titleEnd,
  descriptionEnd,
  footerEnd,
  below,
  error,
  emphasized = false,
  mutedTitle = false,
  leading,
  checkbox,
  trailing,
  changeStats,
  isLoading = false,
  size: sizeProp,
  rootDataAttrs,
}: SelectableListItemRowProps) {
  const listSize = useSelectableListStyle().size;
  const size = sizeProp ?? listSize;
  const descriptionIsString = typeof description === "string";
  const descriptionString = descriptionIsString ? description.trim() : "";
  const hasDescriptionBody =
    Boolean(error?.trim()) ||
    (descriptionIsString ? Boolean(descriptionString) : description != null);
  // Loading, the row draws the tree it always does, each piece of it wrapped
  // in ``Skeleton`` where it stands - so every bar is the size of what
  // it hides, and the row the size of the row it stands for.
  const piece = (node: ReactNode) =>
    isLoading && node != null && node !== false ? (
      <Skeleton
        loading
        variant="rectangular"
        className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
      >
        {node}
      </Skeleton>
    ) : (
      node
    );
  const pieceEnd = (end: SelectableListItemRowEnd | null) =>
    end && isLoading
      ? { delta: piece(end.delta), value: piece(end.value) }
      : end;
  const descriptionTrailing = pieceEnd(normalizeRowEnd(descriptionEnd));
  const showDescription = hasDescriptionBody || descriptionTrailing != null;
  const showFooter = footerEnd != null;
  const showBelow = below != null;
  const descriptionText = error?.trim() || descriptionString || "\u00a0";
  const summaryText = summary?.trim() || null;
  const showSummary = summaryText != null;
  const showChangeStats = changeStats != null;
  const showOverline = overline != null || overlineEnd != null;
  // Description + chips share one row when both are present (FieldReferences
  // sit left, enums/chips sit right).
  const footerSharesDescriptionRow =
    showFooter && showDescription && descriptionTrailing == null;
  // Shared description+chips row is chip-tall (``FOOTER_LINE_HEIGHT``), not a
  // single description line — bump so the minHeights match what it holds.
  const contentHeight = (() => {
    const base = getSelectableListItemRowContentHeight(
      showDescription ? descriptionMaxLines : 0,
      {
        summaryMaxLines: showSummary ? summaryMaxLines : 0,
        overline: showOverline,
        footer: showFooter && !footerSharesDescriptionRow,
      },
    );
    if (footerSharesDescriptionRow && descriptionMaxLines <= 1) {
      return (
        base - descriptionBlockHeight(descriptionMaxLines) + FOOTER_LINE_HEIGHT
      );
    }
    return base;
  })();

  const overlineTrailing = pieceEnd(normalizeRowEnd(overlineEnd));
  const titleTrailingBase = normalizeRowEnd(titleEnd);
  const titleTrailing: SelectableListItemRowEnd | null = pieceEnd(
    showChangeStats
      ? {
          delta: titleTrailingBase?.delta,
          value: (
            <div className="flex items-baseline justify-end gap-2">
              {titleTrailingBase?.value}
              <SelectableListItemRowChangeStack stats={changeStats} />
            </div>
          ),
        }
      : titleTrailingBase,
  );

  const useDeltaGrid =
    overlineTrailing?.delta != null ||
    titleTrailing?.delta != null ||
    descriptionTrailing?.delta != null;
  const hasAnyTrailing =
    overlineTrailing != null ||
    titleTrailing != null ||
    descriptionTrailing != null ||
    trailing != null ||
    showFooter;
  // ``trailing`` rides in the first row's trailing cell. Rows with nothing
  // trailing span the full width, so their text ellipsizes at the row's edge
  // rather than at the trailing column.
  const titleAction = showOverline ? undefined : trailing;
  const overlineSpans = overlineTrailing == null && trailing == null;
  const titleSpans = titleTrailing == null && titleAction == null;
  const descriptionSpans = descriptionTrailing == null;
  const small = size === "xs" || size === "sm";
  const titleLevel = small
    ? "title-sm"
    : size === "lg"
      ? "title-lg"
      : "title-md";
  const descriptionLevel = small
    ? "body-sm"
    : size === "lg"
      ? "body-lg"
      : "body-md";

  const checkboxNode = checkbox ? (
    <Checkbox
      checked={checkbox.checked}
      onClick={(e) => e.stopPropagation()}
      className="shrink-0"
      onCheckedChange={() => checkbox.onToggle?.()}
      tabIndex={-1}
      onKeyDown={(event) => {
        if (event.key === " " || event.key === "Spacebar") {
          event.preventDefault();
        }
      }}
    />
  ) : null;
  // In the overline's row where there is one; else before the words.
  const isCheckboxInOverline = leading == null && showOverline;
  const leadingControl = piece(
    leading ??
      (isCheckboxInOverline || !checkboxNode ? null : (
        <div className="shrink-0 self-start">{checkboxNode}</div>
      )),
  );

  const titleNode =
    typeof title === "string" ? (
      <Typography
        level={titleLevel}
        data-selectable-list-title
        className={cn(
          "min-w-0 [line-height:22px] text-ellipsis overflow-hidden whitespace-nowrap",
          mutedTitle ? "text-muted-foreground" : "text-foreground",
          emphasized && "[font-weight:600]",
        )}
      >
        <Skeleton loading={isLoading} variant="inline">
          {title}
        </Skeleton>
      </Typography>
    ) : (
      <div className="min-w-0 flex items-baseline min-h-5.5 overflow-hidden">
        {piece(title)}
      </div>
    );

  const descriptionNode = !showDescription ? null : descriptionIsString ||
    error != null ? (
    <Typography
      level={descriptionLevel}
      className={cn(
        "min-w-0 [line-height:20px]",
        error ? "text-destructive" : "text-muted-foreground",
        descriptionMaxLines > 1
          ? descriptionMaxLines === 2
            ? "line-clamp-2 whitespace-normal"
            : "line-clamp-none whitespace-normal"
          : "text-ellipsis overflow-hidden whitespace-nowrap",
      )}
    >
      <Skeleton loading={isLoading} variant="inline">
        {descriptionText}
      </Skeleton>
    </Typography>
  ) : description != null ? (
    <div
      // Block layout so the contact field's baseline can participate in the
      // shared description+chips row (flex wrappers steal the baseline).
      className={cn(
        "min-w-0 block",
        descriptionMaxLines === 1 ? "min-h-5" : "min-h-10",
        "overflow-hidden",
      )}
    >
      {piece(description)}
    </div>
  ) : null;

  return (
    <div
      {...rootDataAttrs}
      className="flex items-start gap-2.5 min-w-0 min-h-(--selectable-list-item-row-content-height)"
      style={
        {
          "--selectable-list-item-row-content-height": `${contentHeight}px`,
        } as CSSProperties
      }
    >
      {leadingControl}
      <div
        // Its trailing cells 6px from its text, its rows ``CONTENT_GAP`` apart.
        className={cn(
          "flex-1 min-w-0 grid",
          !hasAnyTrailing
            ? "grid-cols-[minmax(0,1fr)]"
            : useDeltaGrid
              ? "grid-cols-[minmax(0,1fr)_auto_auto]"
              : "grid-cols-[minmax(0,1fr)_auto]",
          hasAnyTrailing ? "gap-x-1.5" : "gap-x-0",
          "gap-y-0 items-baseline min-h-(--selectable-list-item-row-content-height)",
        )}
      >
        {showOverline ? (
          <>
            <div
              className={cn(
                overlineSpans && "col-span-full",
                "min-w-0 min-h-4 flex items-baseline gap-1.5 overflow-hidden",
              )}
            >
              {/* Centred on the line, the same box loading or not: what
                  stands in for it while it loads is inside it. */}
              {isCheckboxInOverline && checkboxNode ? (
                <div className="shrink-0 self-center flex">
                  {piece(checkboxNode)}
                </div>
              ) : null}
              {piece(overline)}
            </div>
            {overlineSpans ? null : (
              <TrailingCells
                end={overlineTrailing}
                useDeltaGrid={useDeltaGrid}
                action={piece(trailing)}
              />
            )}
          </>
        ) : null}

        <div
          className={cn(
            titleSpans && "col-span-full",
            "min-w-0 min-h-5.5 flex items-baseline overflow-hidden",
          )}
        >
          {titleNode}
        </div>
        {titleSpans ? null : (
          <TrailingCells
            end={titleTrailing}
            useDeltaGrid={useDeltaGrid}
            action={piece(titleAction)}
          />
        )}

        {showDescription || (showFooter && footerSharesDescriptionRow) ? (
          <>
            <div
              className={cn(
                !footerSharesDescriptionRow &&
                  descriptionSpans &&
                  "col-span-full",
                "min-w-0",
                footerSharesDescriptionRow
                  ? descriptionMaxLines > 1
                    ? descriptionMaxLines === 1
                      ? "min-h-5"
                      : "min-h-10"
                    : "min-h-7"
                  : descriptionMaxLines === 1
                    ? "min-h-5"
                    : "min-h-10",
                "flex",
                footerSharesDescriptionRow ? "items-baseline" : "items-center",
                "overflow-hidden",
              )}
            >
              {descriptionNode}
            </div>
            {footerSharesDescriptionRow ? (
              <>
                {useDeltaGrid ? <div /> : null}
                <div className="[justify-self:end] self-baseline text-right min-w-0 flex items-baseline justify-end flex-wrap gap-1 min-h-7">
                  {piece(footerEnd)}
                </div>
              </>
            ) : descriptionSpans ? null : (
              <TrailingCells
                end={descriptionTrailing}
                useDeltaGrid={useDeltaGrid}
              />
            )}
          </>
        ) : null}

        {showFooter && !footerSharesDescriptionRow ? (
          <>
            <div className="min-w-0 min-h-7" />
            {useDeltaGrid ? <div /> : null}
            <div className="[justify-self:end] self-baseline text-right min-w-0 flex items-center justify-end flex-wrap gap-1 min-h-7">
              {piece(footerEnd)}
            </div>
          </>
        ) : null}

        {showSummary ? (
          <div
            className={cn(
              "col-span-full min-w-0",
              showSummary ? (summaryMaxLines === 1 ? "h-4.5" : "h-9") : "h-0",
              "shrink-0",
            )}
          >
            <SelectableListItemRowItalicReply
              maxLines={summaryMaxLines}
              isLoading={isLoading}
            >
              {summaryText}
            </SelectableListItemRowItalicReply>
          </div>
        ) : null}

        {showBelow ? (
          <div
            onClickCapture={(e) => e.stopPropagation()}
            className="col-span-full min-w-0 pt-1.5"
          >
            {piece(below)}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Italic reply line with return icon: the row's own ``summary`` (thread list summaries). */
function SelectableListItemRowItalicReply({
  children,
  maxLines = 2,
  isLoading,
  "data-testid": dataTestId,
}: {
  children: ReactNode;
  maxLines?: 1 | 2 | 3;
  isLoading: boolean;
  "data-testid"?: string;
}) {
  // Thread summaries arrive from the backend already led by "↪".
  const hasOwnArrow = typeof children === "string" && children.startsWith("↪");
  return (
    <div className="flex items-start gap-1 min-w-0">
      {!hasOwnArrow && (
        <div
          aria-hidden
          className="text-muted-foreground/80 flex shrink-0 [line-height:0] mt-0.5"
        >
          <Skeleton
            loading={isLoading}
            variant="rectangular"
            className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
          >
            <Icon ReactIcon={CornerDownRightIcon} size="sm" />
          </Skeleton>
        </div>
      )}
      <Typography
        level="body-sm"
        data-testid={dataTestId}
        className={cn(
          "min-w-0 [line-height:18px] text-muted-foreground/80 italic whitespace-normal",
          // Tailwind's clamp, which a Typography's ``block`` yields to: an
          // arbitrary display beside it lost, and the words ran on.
          maxLines === 1
            ? "line-clamp-1"
            : maxLines === 2
              ? "line-clamp-2"
              : "line-clamp-3",
        )}
      >
        <Skeleton loading={isLoading} variant="inline">
          {children}
        </Skeleton>
      </Typography>
    </div>
  );
}

/** Shared overline label — hoverable link that opens the node panel when ``nodeId`` is set. */
export function SelectableListItemRowOverline({
  children,
  nodeId,
  keyboardActive = false,
}: {
  children: ReactNode;
  nodeId?: string | null;
  /** When false, keep the control out of the list's Tab/Enter focus cycle. */
  keyboardActive?: boolean;
}) {
  if (!nodeId) {
    return (
      <Typography
        level="body-xs"
        className="truncate min-w-0 max-w-full [line-height:16px] text-muted-foreground/80 overflow-hidden text-ellipsis whitespace-nowrap"
      >
        {children}
      </Typography>
    );
  }

  return (
    <Typography
      component="button"
      type="button"
      level="body-xs"
      tabIndex={keyboardActive ? 0 : -1}
      onMouseDown={(event) => {
        // Prevent focus steal + row selection clickthrough.
        event.preventDefault();
        event.stopPropagation();
      }}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        const state = State.getState();
        // Same node already showing → close. Otherwise open (or switch to) it.
        if (state.isNodePanelOpen && state.activePanelNodeId === nodeId) {
          state.setIsNodePanelOpen(false);
          return;
        }
        state.openNodePanel(nodeId, { autofocus: false });
      }}
      className="min-w-0 max-w-full [line-height:16px] text-muted-foreground/80 overflow-hidden text-ellipsis whitespace-nowrap inline align-baseline no-underline [text-decoration-thickness:initial] [text-decoration-style:initial] decoration-[initial] cursor-pointer [border:none] [background:none] p-0 outline-[initial] [outline-style:none] [outline-width:initial] hover:text-muted-foreground hover:underline hover:[text-decoration-thickness:initial] hover:[text-decoration-style:initial] hover:decoration-[currentColor] hover:underline-offset-2 hover:outline-[initial] [&:hover]:[outline-style:none] hover:[outline-width:initial] focus:text-muted-foreground focus:underline focus:[text-decoration-thickness:initial] focus:[text-decoration-style:initial] focus:decoration-[currentColor] focus:underline-offset-2 focus:outline-[initial] [&:focus]:[outline-style:none] focus:[outline-width:initial] focus-visible:text-muted-foreground focus-visible:underline focus-visible:[text-decoration-thickness:initial] focus-visible:[text-decoration-style:initial] focus-visible:decoration-[currentColor] focus-visible:underline-offset-2 focus-visible:outline-[initial] [&:focus-visible]:[outline-style:none] focus-visible:[outline-width:initial]"
    >
      {children}
    </Typography>
  );
}

/** Shared typography for trailing dates / timestamps. */
export function SelectableListItemRowTitleEnd({
  children,
  monospace = false,
  size: sizeProp,
}: {
  children: ReactNode;
  monospace?: boolean;
  /** Read from the list the row is in unless given. */
  size?: SelectableListSize;
}) {
  const listSize = useSelectableListStyle().size;
  const size = sizeProp ?? listSize;
  const small = size === "xs" || size === "sm";
  return (
    <Typography
      level={small ? "body-xs" : "body-sm"}
      className={cn(
        "min-w-fit [line-height:18px]",
        small ? "text-[11px]" : "text-xs",
        "[font-weight:500] whitespace-nowrap text-muted-foreground/80",
        monospace && "font-mono [font-variant-numeric:tabular-nums]",
      )}
    >
      {children}
    </Typography>
  );
}

/** Emphasized trailing value (e.g. currency on triage rows). */
export function SelectableListItemRowTitleEndEmphasis({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <Typography
      // The title's size: an amount beside a title is not a size above it.
      level="mono-body-md"
      className="min-w-fit [line-height:22px] [font-weight:600] [font-variant-numeric:tabular-nums] text-foreground"
    >
      {children}
    </Typography>
  );
}

/** Right-aligned delta / exact-match indicator for the shared trailing grid. */
export function SelectableListItemRowDelta({
  exact = false,
  label,
}: {
  exact?: boolean;
  label?: string | null;
}) {
  if (exact) {
    return (
      <Icon
        ReactIcon={CheckIcon}
        size="sm"
        style={{
          color: "var(--foreground)",
          flexShrink: 0,
          display: "inline-block",
          verticalAlign: "baseline",
        }}
      />
    );
  }
  if (!label) {
    return null;
  }
  return (
    <Typography
      component="span"
      level="body-xs"
      className="text-muted-foreground/80 [font-variant-numeric:tabular-nums] whitespace-nowrap"
    >
      {label}
    </Typography>
  );
}
